/**
 * Mini cart drawer — Theme Blueprint
 * ---------------------------------------------------------------------------
 * Vanilla JS. Opens a slide-in drawer, renders /cart.js line items (including
 * visible line-item properties), and supports quantity change / remove via
 * /cart/change.js. Refreshes + opens when a `cart:updated` event fires
 * (dispatched by any add-to-cart flow). UI copy comes from the
 * [data-theme-strings] JSON block in theme.liquid.
 */
(function () {
  "use strict";

  var root = document.querySelector("[data-mini-cart]");
  if (!root) return;

  var themeStrings = (function () {
    var el = document.querySelector("[data-theme-strings]");
    if (!el) return {};
    try {
      return JSON.parse(el.textContent) || {};
    } catch (e) {
      return {};
    }
  })();
  var t = themeStrings.miniCart || {};

  function removeItemLabel(title) {
    var template = t.removeItem || "Remove __TITLE__";
    return template.replace("__TITLE__", title);
  }

  var moneyFormat = root.getAttribute("data-money-format") || "€{{amount_with_comma_separator}}";
  var body = root.querySelector("[data-cart-body]");
  var foot = root.querySelector("[data-cart-foot]");
  var subtotalEl = root.querySelector("[data-cart-subtotal]");
  var emptyEl = root.querySelector("[data-cart-empty]");
  var countEls = document.querySelectorAll("[data-cart-count]");
  var lastFocus = null;
  var busy = false;

  function cartUrl(path) {
    var base = window.Shopify && window.Shopify.routes && window.Shopify.routes.root ? window.Shopify.routes.root : "/";
    return base.replace(/\/$/, "") + path;
  }

  /* ---- Money formatting (Shopify style) ------------------------------- */
  function formatMoney(cents) {
    var placeholder = /\{\{\s*(\w+)\s*\}\}/;
    function delim(number, precision, thousands, decimal) {
      if (isNaN(number) || number == null) return "0";
      number = (number / 100.0).toFixed(precision == null ? 2 : precision);
      var parts = number.split(".");
      var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, "$1" + (thousands == null ? "," : thousands));
      return whole + (parts[1] ? (decimal == null ? "." : decimal) + parts[1] : "");
    }
    var match = moneyFormat.match(placeholder);
    var token = match ? match[1] : "amount";
    var value;
    switch (token) {
      case "amount_no_decimals":
        value = delim(cents, 0);
        break;
      case "amount_with_comma_separator":
        value = delim(cents, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        value = delim(cents, 0, ".", ",");
        break;
      case "amount_with_apostrophe_separator":
        value = delim(cents, 2, "'", ".");
        break;
      default:
        value = delim(cents, 2);
    }
    return moneyFormat.replace(placeholder, value);
  }

  /* ---- Open / close --------------------------------------------------- */
  function open() {
    root.classList.add("is-open");
    // Blendet Zenchefs Reservieren-Chip aus, siehe Mini-cart-CSS in header.liquid.
    document.documentElement.classList.add("mini-cart-is-open");
    if (window.lenis) window.lenis.stop();
    else document.documentElement.style.overflow = "hidden";
    lastFocus = document.activeElement;
    var closeBtn = root.querySelector("[data-cart-close]");
    if (closeBtn) closeBtn.focus();
  }

  function close() {
    root.classList.remove("is-open");
    document.documentElement.classList.remove("mini-cart-is-open");
    if (window.lenis) window.lenis.start();
    else document.documentElement.style.overflow = "";
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  /* ---- Rendering ------------------------------------------------------ */
  function escapeHtml(s) {
    return String(s == null ? "" : s).replace(/[&<>"']/g, function (c) {
      return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c];
    });
  }

  function propertiesHtml(props) {
    if (!props) return "";
    var out = "";
    Object.keys(props).forEach(function (key) {
      if (key.charAt(0) === "_") return; // hidden property
      var val = props[key];
      if (val == null || val === "") return;
      out += '<span class="mini-cart__item-meta">' + escapeHtml(key) + ": " + escapeHtml(val) + "</span>";
    });
    return out;
  }

  function itemHtml(item) {
    var img = item.image
      ? '<div class="mini-cart__item-media"><img src="' +
        item.image.replace(/(\.[a-z]+)(\?|$)/i, "_120x$1$2") +
        '" alt="" width="64" height="64" loading="lazy"></div>'
      : '<div class="mini-cart__item-media"></div>';
    var variant =
      item.variant_title && item.variant_title !== "Default Title"
        ? '<span class="mini-cart__item-meta">' + escapeHtml(item.variant_title) + "</span>"
        : "";
    return (
      '<div class="mini-cart__item" data-line-item data-key="' +
      escapeHtml(item.key) +
      '">' +
      img +
      '<div class="mini-cart__item-info">' +
      '<a class="mini-cart__item-title" href="' +
      escapeHtml(item.url) +
      '">' +
      escapeHtml(item.product_title) +
      "</a>" +
      variant +
      propertiesHtml(item.properties) +
      '<div class="mini-cart__qty">' +
      '<button type="button" data-qty-dec aria-label="' +
      escapeHtml(t.decrease || "Decrease") +
      '">&#8722;</button>' +
      "<span>" +
      item.quantity +
      "</span>" +
      '<button type="button" data-qty-inc aria-label="' +
      escapeHtml(t.increase || "Increase") +
      '">+</button>' +
      "</div>" +
      "</div>" +
      '<div class="mini-cart__item-side">' +
      '<span class="mini-cart__item-price">' +
      formatMoney(item.final_line_price) +
      "</span>" +
      '<button type="button" class="mini-cart__remove" data-remove aria-label="' +
      escapeHtml(removeItemLabel(item.product_title)) +
      '">' +
      '<svg class="mini-cart__remove-icon" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
      '<path d="M4 7h16" />' +
      '<path d="M10 11v6M14 11v6" />' +
      '<path d="M6 7l1 12a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1l1-12" />' +
      '<path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />' +
      "</svg>" +
      "<span>" +
      escapeHtml(t.remove || "Remove") +
      "</span>" +
      "</button>" +
      "</div>" +
      "</div>"
    );
  }

  function render(cart) {
    countEls.forEach(function (el) {
      el.textContent = cart.item_count;
      if (cart.item_count > 0) el.removeAttribute("hidden");
      else el.setAttribute("hidden", "");
    });

    // The cart chip itself only shows once something is in the cart.
    document.querySelectorAll("[data-cart-toggle]").forEach(function (btn) {
      if (cart.item_count > 0) btn.removeAttribute("data-cart-empty");
      else btn.setAttribute("data-cart-empty", "");
    });

    if (!cart.items || cart.items.length === 0) {
      body.innerHTML = '<p class="mini-cart__empty">' + escapeHtml(t.empty || "Your cart is empty.") + "</p>";
      if (foot) foot.hidden = true;
      return;
    }
    body.innerHTML = cart.items.map(itemHtml).join("");
    if (subtotalEl) subtotalEl.textContent = formatMoney(cart.items_subtotal_price);
    if (foot) foot.hidden = false;
  }

  /* ---- Data ----------------------------------------------------------- */
  function fetchCart() {
    return fetch(cartUrl("/cart.js"), { headers: { Accept: "application/json" } })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        render(cart);
        return cart;
      })
      .catch(function () {});
  }

  /* ---- Quantity changes (debounced + optimistic) ---------------------- */
  // Clicking +/- updates the visible quantity instantly and coalesces a burst
  // of clicks into a single /cart/change.js call once the user pauses — no
  // waiting on the AJAX round-trip between clicks. `quantity` is absolute.
  var DEBOUNCE_MS = 400;
  var timers = {}; // line key -> pending debounce timeout
  var queued = {}; // line key -> latest target quantity waiting on the in-flight request

  function setLoading(key, on) {
    var el = body && body.querySelector('[data-line-item][data-key="' + key + '"]');
    if (el) el.classList.toggle("is-loading", on);
  }

  function sendChange(key, quantity) {
    if (busy) {
      // A request is already running — remember the latest target and send it
      // once the current one settles.
      queued[key] = quantity;
      return;
    }
    busy = true;
    root.classList.add("is-busy");
    setLoading(key, true);
    fetch(cartUrl("/cart/change.js"), {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        render(cart);
        document.dispatchEvent(new CustomEvent("cart:refreshed"));
      })
      .catch(function () {})
      .finally(function () {
        busy = false;
        root.classList.remove("is-busy");
        setLoading(key, false);
        // Flush a change queued while this request was in flight (latest wins).
        var keys = Object.keys(queued);
        if (keys.length) {
          var next = keys[0];
          var qty = queued[next];
          delete queued[next];
          sendChange(next, qty);
        }
      });
  }

  function requestChange(key, quantity) {
    clearTimeout(timers[key]);
    timers[key] = setTimeout(function () {
      delete timers[key];
      sendChange(key, quantity);
    }, DEBOUNCE_MS);
  }

  /* ---- Events --------------------------------------------------------- */
  document.addEventListener("click", function (e) {
    var toggle = e.target.closest("[data-cart-toggle]");
    if (toggle) {
      e.preventDefault();
      fetchCart();
      open();
      return;
    }
    if (e.target.closest("[data-cart-close]")) {
      close();
      return;
    }

    var lineEl = e.target.closest("[data-line-item]");
    if (!lineEl) return;
    var key = lineEl.getAttribute("data-key");
    var qtyEl = lineEl.querySelector(".mini-cart__qty span");
    var qty = parseInt(qtyEl ? qtyEl.textContent : "1", 10) || 1;

    if (e.target.closest("[data-qty-inc]")) {
      if (qtyEl) qtyEl.textContent = qty + 1; // optimistic
      requestChange(key, qty + 1);
    } else if (e.target.closest("[data-qty-dec]")) {
      var next = Math.max(0, qty - 1);
      if (next === 0)
        lineEl.classList.add("is-loading"); // optimistic remove
      else if (qtyEl) qtyEl.textContent = next; // optimistic
      requestChange(key, next);
    } else if (e.target.closest("[data-remove]")) {
      lineEl.classList.add("is-loading"); // optimistic remove
      requestChange(key, 0);
    }
  });

  document.addEventListener("keydown", function (e) {
    if (e.key === "Escape" && root.classList.contains("is-open")) close();
  });

  // Configurator (and any add-to-cart) dispatches this → refresh + open.
  document.addEventListener("cart:updated", function () {
    fetchCart().then(open);
  });

  // Keep the count badge in sync on load.
  fetchCart();
})();
